import { useMutation } from "@apollo/client";
import { UserOption } from "components/MultipleUserPicker";
import {
  HARMONIZED_VIOLATION_DEFAULT_ASSIGNEES,
  HARMONIZED_VIOLATION_STATS,
  HARMONIZED_VIOLATIONS,
  SET_HARMONIZED_VIOLATION_BUSINESS_UNIT_DEFAULT_ASSIGNEE,
  SET_HARMONIZED_VIOLATION_REGION_DEFAULT_ASSIGNEE,
} from "hooks/useHarmonizedViolations";

import { type ViolationRow } from "./columns";
import { ScopeDefaultButton } from "./ScopeDefaultButton";
import { useSetDefaultAssignee } from "./useSetDefaultAssignee";
import { toUserOption, type ViolationDefaultAssignees } from "./violationDefaultAssignees";

/** One scope button's state: its copy label, scope value, current default, and save. */
type ScopeDefault = {
  label: string;
  value: string;
  currentDefault: UserOption | null;
  saving: boolean;
  save: (userId: string | null) => Promise<boolean>;
};

/**
 * The region and business-unit "set default" popover buttons — gated like the
 * API (staff or WriteAllFacility) and refetching the scope-affected queries.
 */
export function ScopeDefaultButtons({
  violation,
  tenantId,
  canSetScopeDefault,
  defaults,
}: {
  violation: ViolationRow;
  tenantId: string;
  canSetScopeDefault: boolean;
  /** Null while the current defaults are loading; the popovers wait for them. */
  defaults: ViolationDefaultAssignees | null;
}) {
  const setDefaultAssignee = useSetDefaultAssignee();
  const refetchList = {
    refetchQueries: [
      HARMONIZED_VIOLATIONS,
      HARMONIZED_VIOLATION_STATS,
      HARMONIZED_VIOLATION_DEFAULT_ASSIGNEES,
    ],
    awaitRefetchQueries: true,
  };
  const [setBusinessUnitDefault, { loading: savingBusinessUnitDefault }] = useMutation(
    SET_HARMONIZED_VIOLATION_BUSINESS_UNIT_DEFAULT_ASSIGNEE,
    refetchList
  );
  const [setRegionDefault, { loading: savingRegionDefault }] = useMutation(
    SET_HARMONIZED_VIOLATION_REGION_DEFAULT_ASSIGNEE,
    refetchList
  );

  // Checked after the hooks (never conditionally) so hook order stays stable.
  if (!canSetScopeDefault) return null;

  const businessUnit = violation.facility.businessUnit?.trim();
  const region = violation.facility.department?.trim();

  // Listed narrowest scope first, matching the assignee precedence the drawer's
  // facility default sits at the top of: facility, then region, then business
  // unit. A blank scope is no scope: a button reading "business unit:  " would
  // set a default nobody could find again.
  const scopes: ScopeDefault[] = [];
  if (region) {
    scopes.push({
      label: "region",
      value: region,
      currentDefault: toUserOption(defaults?.region?.user),
      saving: savingRegionDefault,
      save: async (userId) => {
        const { data } = await setRegionDefault({
          variables: { tenantId, region, userId },
        });
        return userId ? Boolean(data?.setHarmonizedViolationRegionDefaultAssignee) : true;
      },
    });
  }
  if (businessUnit) {
    scopes.push({
      label: "business unit",
      value: businessUnit,
      currentDefault: toUserOption(defaults?.businessUnit?.user),
      saving: savingBusinessUnitDefault,
      save: async (userId) => {
        const { data } = await setBusinessUnitDefault({
          variables: { tenantId, businessUnit, userId },
        });
        return userId ? Boolean(data?.setHarmonizedViolationBusinessUnitDefaultAssignee) : true;
      },
    });
  }

  const runScopeDefault = (scope: ScopeDefault, userId: string | null) =>
    setDefaultAssignee({
      run: () => scope.save(userId),
      successMessage: userId
        ? `Default assignee set for ${scope.label} ${scope.value}`
        : `Default assignee cleared for ${scope.label} ${scope.value}`,
      // The copy names the scope that didn't save, since both scopes share this handler.
      errorMessage: userId
        ? `Could not set the ${scope.label} default assignee. Try again.`
        : `Could not clear the ${scope.label} default assignee. Try again.`,
    });

  return (
    <>
      {scopes.map((scope) => (
        <ScopeDefaultButton
          key={scope.label}
          buttonText={`Set default for ${scope.label}: ${scope.value}`}
          pickerLabel="Default assignee"
          currentDefault={scope.currentDefault}
          saving={scope.saving}
          disabled={!defaults}
          onSave={(userId) => runScopeDefault(scope, userId)}
          onClear={() => runScopeDefault(scope, null)}
        />
      ))}
    </>
  );
}
